import type {
	Choice,
	Field,
	StepI,
	TriggerChoice,
	WizardFormConfig,
} from "@CommonLib/src/frontend/WizardForm/types";
import {
	findChildren,
	getFieldFromId,
	getStepFromId,
	lintWizardFormConfig,
	stepsUsingStepAsCondition,
} from "@CommonLib/src/frontend/WizardForm/utils";

// Generic utility to update one step by id
export function updateStep(
	config: WizardFormConfig,
	stepId: number,
	updater: (step: StepI) => StepI,
): WizardFormConfig {
	return {
		...config,
		steps: config.steps.map((s) => (s.id === stepId ? updater(s) : s)),
	};
}

// Generic array move helper
export function moveItemUp<T extends { index: number; id: number }>(
	items: T[],
	itemId: number,
): T[] {
	const item = items.find((i) => i.id === itemId);
	if (!item) throw new Error(`Item ${itemId} not found`);

	const neighbor = items.find((i) => i.index === item.index - 1);
	if (!neighbor) throw new Error(`No item above index ${item.index}`);

	const itemsReindexed = items.map((i) => {
		if (i.id === item.id) return { ...i, index: neighbor.index };
		if (i.id === neighbor.id) return { ...i, index: item.index };
		return i;
	});
	itemsReindexed.sort((a, b) => a.index - b.index);
	return itemsReindexed;
}

export function moveItemDown<T extends { index: number; id: number }>(
	items: T[],
	itemId: number,
): T[] {
	const item = items.find((i) => i.id === itemId);
	if (!item) throw new Error(`Item ${itemId} not found`);

	const neighbor = items.find((i) => i.index === item.index + 1);
	if (!neighbor) throw new Error(`No item below index ${item.index}`);

	const itemsReindexed = items.map((i) => {
		if (i.id === item.id) return { ...i, index: neighbor.index };
		if (i.id === neighbor.id) return { ...i, index: item.index };
		return i;
	});
	itemsReindexed.sort((a, b) => a.index - b.index);
	return itemsReindexed;
}

export function deleteAndReindex<T extends { index: number; id: number }>(
	items: T[],
	itemId: number,
): T[] {
	// Remove the item first
	const remaining = items.filter((i) => i.id !== itemId);
	// Reindex sequentially
	return remaining
		.sort((a, b) => a.index - b.index)
		.map((item, idx) => ({ ...item, index: idx }));
}

export function getNextAvailableId<T extends { id: number }>(
	items: T[],
): number {
	if (items.length === 0) return 1;
	const ids = items.map((item) => item.id);
	ids.sort();
	return Math.max(...ids) + 1;
}

const getStepNextFieldIndex = (config: WizardFormConfig, stepId: number) => {
	const step = getStepFromId(config, stepId);
	const fieldIndices = step.fields.map((f) => f.index);
	return fieldIndices.length > 0 ? Math.max(...fieldIndices) + 1 : 0;
};

const getStepNextFieldIdentifier = (
	config: WizardFormConfig,
	stepId: number,
) => {
	const step = getStepFromId(config, stepId);
	const fieldIdentifiers = step.fields.map((f) => f.identifier);
	return fieldIdentifiers.length > 0 ? Math.max(...fieldIdentifiers) + 1 : 0;
};

const getFieldNextChoiceIndex = (
	config: WizardFormConfig,
	stepId: number,
	fieldId: number,
) => {
	const step = config.steps.find((s) => s.id === stepId);
	if (!step) throw Error();
	const field = step.fields.find((f) => f.id === fieldId);
	if (!field) throw Error();
	const choiceIndices = field.choices?.map((c) => c.index) ?? [];
	return choiceIndices.length > 0 ? Math.max(...choiceIndices) + 1 : 0;
};

// ---- Field actions ----
export function addField(
	config: WizardFormConfig,
	stepId: number,
	newField: Omit<Field, "index" | "id" | "identifier">,
): WizardFormConfig {
	const step = getStepFromId(config, stepId);
	const newFieldWithIndex: Field = {
		...newField,
		id: getNextAvailableId(step.fields),
		index: getStepNextFieldIndex(config, stepId),
		identifier: getStepNextFieldIdentifier(config, stepId),
	};
	const updatedConfig = updateStep(config, stepId, (step) => ({
		...step,
		fields: [...step.fields, newFieldWithIndex],
	}));
	lintWizardFormConfig(updatedConfig);
	return updatedConfig;
}

export function deleteField(
	config: WizardFormConfig,
	stepId: number,
	fieldId: number,
): WizardFormConfig {
	const updatedConfig = updateStep(config, stepId, (step) => ({
		...step,
		fields: deleteAndReindex(step.fields, fieldId),
	}));
	lintWizardFormConfig(updatedConfig);
	return updatedConfig;
}

export function moveFieldUp(
	config: WizardFormConfig,
	stepId: number,
	fieldId: number,
): WizardFormConfig {
	const updatedConfig = updateStep(config, stepId, (step) => ({
		...step,
		fields: moveItemUp(step.fields, fieldId),
	}));
	lintWizardFormConfig(updatedConfig);
	return updatedConfig;
}

export function moveFieldDown(
	config: WizardFormConfig,
	stepId: number,
	fieldId: number,
): WizardFormConfig {
	const updatedConfig = updateStep(config, stepId, (step) => ({
		...step,
		fields: moveItemDown(step.fields, fieldId),
	}));
	lintWizardFormConfig(updatedConfig);
	return updatedConfig;
}

// ---- Choice actions ----
export function addChoice(
	config: WizardFormConfig,
	stepId: number,
	fieldId: number,
	newChoice: Omit<Choice, "index" | "id">,
): WizardFormConfig {
	const field = getFieldFromId(config, stepId, fieldId);
	const choices = field.choices;
	if (!choices) throw Error();
	const newChoiceWithIndex: Choice = {
		...newChoice,
		id: getNextAvailableId(choices),
		index: getFieldNextChoiceIndex(config, stepId, fieldId),
	};
	const updatedConfig = updateStep(config, stepId, (step) => ({
		...step,
		fields: step.fields.map((f) =>
			f.id === fieldId
				? { ...f, choices: [...(f.choices ?? []), newChoiceWithIndex] }
				: f,
		),
	}));
	lintWizardFormConfig(updatedConfig);
	return updatedConfig;
}

export function deleteChoice(
	config: WizardFormConfig,
	stepId: number,
	fieldId: number,
	choiceId: number,
): WizardFormConfig {
	const updatedConfig = updateStep(config, stepId, (step) => ({
		...step,
		fields: step.fields.map((f) =>
			f.id === fieldId
				? { ...f, choices: deleteAndReindex(f.choices ?? [], choiceId) }
				: f,
		),
	}));
	lintWizardFormConfig(updatedConfig);
	return updatedConfig;
}

export function moveChoiceUp(
	config: WizardFormConfig,
	stepId: number,
	fieldId: number,
	choiceId: number,
): WizardFormConfig {
	const updatedConfig = updateStep(config, stepId, (step) => ({
		...step,
		fields: step.fields.map((f) =>
			f.id === fieldId
				? { ...f, choices: moveItemUp(f.choices ?? [], choiceId) }
				: f,
		),
	}));
	lintWizardFormConfig(updatedConfig);
	return updatedConfig;
}

export function moveChoiceDown(
	config: WizardFormConfig,
	stepId: number,
	fieldId: number,
	choiceId: number,
): WizardFormConfig {
	const updatedConfig = updateStep(config, stepId, (step) => ({
		...step,
		fields: step.fields.map((f) =>
			f.id === fieldId
				? { ...f, choices: moveItemDown(f.choices ?? [], choiceId) }
				: f,
		),
	}));
	lintWizardFormConfig(updatedConfig);
	return updatedConfig;
}

// ---- Step actions ----
export function addStep(
	config: WizardFormConfig,
	parentStepId: number | null,
	newStep: Omit<StepI, "id" | "parentStepId">,
): WizardFormConfig {
	const newId = getNextAvailableId(config.steps);
	const newStepWithId: StepI = {
		...newStep,
		id: newId,
		parentStepId,
	};
	// If parentStepId is null, this means this is the first insertion
	if (parentStepId === null) {
		// It's first step so there can't be others atm
		if (config.steps.length > 0) throw Error();
		const updatedConfig: WizardFormConfig = {
			steps: [newStepWithId],
			configId: config.configId,
		};
		lintWizardFormConfig(updatedConfig);
		return updatedConfig;
	}
	// parentStepId is not null so we are inserting a step after another

	const siblings = stepsUsingStepAsCondition(config, parentStepId);
	if (siblings.length > 0) {
		// We ensure the new step being added has a triggerChoice defined
		const newStepTriggerChoice = newStep.triggerChoice;
		if (!newStepTriggerChoice) {
			throw Error(
				"New step must have a defined triggerChoice defined to match its siblings",
			);
		}
		// We ensure the triggerChoice is consistent with other siblings
		for (const sibling of siblings) {
			const siblingTriggerChoice = sibling.triggerChoice;
			if (!siblingTriggerChoice) throw Error();
			if (
				siblingTriggerChoice.stepId !== newStepTriggerChoice.stepId ||
				siblingTriggerChoice.fieldId !== newStepTriggerChoice.fieldId
			) {
				throw Error(
					"New step must have a triggerChoice pointing to same step/field than its siblings",
				);
			}
		}

		// We just insert the step
		const updatedConfig = {
			steps: [...config.steps, newStepWithId],
			configId: config.configId,
		};
		lintWizardFormConfig(updatedConfig);
		return updatedConfig;
	}

	const children = findChildren(config.steps, parentStepId);
	const child = children[0];
	// if no child, we just insert at the end, no child parentStepId to update
	if (!child) {
		const updatedConfig = {
			steps: [...config.steps, newStepWithId],
			configId: config.configId,
		};
		lintWizardFormConfig(updatedConfig);
		return updatedConfig;
	}

	// We add new step
	const updatedConfig1 = {
		steps: [...config.steps, newStepWithId],
		configId: config.configId,
	};
	// We update parent child parentStepId to use the inserted step id
	const updatedConfig2 = updateStep(updatedConfig1, child.id, (step) => ({
		...step,
		parentStepId: newId,
	}));
	lintWizardFormConfig(updatedConfig2);
	return updatedConfig2;
}

export function deleteStep(
	config: WizardFormConfig,
	stepId: number,
): WizardFormConfig {
	if (stepsUsingStepAsCondition(config, stepId).length > 0)
		throw Error(
			"Can't delete a step that contains a condition for other steps",
		);
	const stepToDelete = getStepFromId(config, stepId);
	const updatedConfig = {
		steps: config.steps
			.filter((s) => s.id !== stepId)
			.map((s) => {
				if (s.parentStepId === stepToDelete.id) {
					return { ...s, parentStepId: stepToDelete.parentStepId };
				}
				return s;
			}),
		configId: config.configId,
	};
	lintWizardFormConfig(updatedConfig);
	return updatedConfig;
}

export function editStep(
	config: WizardFormConfig,
	stepId: number,
	updates: { name?: string; triggerChoice?: TriggerChoice },
): WizardFormConfig {
	const newTriggerChoice = updates.triggerChoice;
	const updatedConfig = {
		steps: config.steps.map((s) => {
			if (newTriggerChoice && newTriggerChoice.stepId === s.id) {
				return {
					...s,
					// if triggerChoice was changed, we ensure the field it points to is required
					fields: s.fields.map((f) => {
						if (f.id !== newTriggerChoice.fieldId) return f;
						return {
							...f,
							required: true,
						};
					}),
				};
			}

			if (s.id !== stepId) return s;

			return {
				...s,
				name: updates.name ? updates.name : s.name,
				triggerChoice: newTriggerChoice ? newTriggerChoice : s.triggerChoice,
			};
		}),
		configId: config.configId,
	};
	lintWizardFormConfig(updatedConfig);
	return updatedConfig;
}

export function editField(
	config: WizardFormConfig,
	stepId: number,
	fieldId: number,
	updates: { label?: string; description?: string; required?: boolean },
): WizardFormConfig {
	const updatedConfig = {
		steps: config.steps.map((s) => {
			if (s.id !== stepId) return s;

			return {
				...s,
				fields: s.fields.map((f) => {
					if (f.id !== fieldId) return f;
					return {
						...f,
						label: updates.label ? updates.label : f.label,
						description: updates.description
							? updates.description
							: f.description,
						required: updates.required ? updates.required : f.required,
					};
				}),
			};
		}),
		configId: config.configId,
	};
	lintWizardFormConfig(updatedConfig);
	return updatedConfig;
}

export function editChoice(
	config: WizardFormConfig,
	stepId: number,
	fieldId: number,
	choiceId: number,
	updates: { name?: string },
): WizardFormConfig {
	const updatedConfig = {
		steps: config.steps.map((s) => {
			if (s.id !== stepId) return s;

			return {
				...s,
				fields: s.fields.map((f) => {
					if (f.id !== fieldId) return f;
					return {
						...f,
						choices: (f.choices ?? []).map((c) => {
							if (c.id !== choiceId) return c;
							return {
								...c,
								name: updates.name ? updates.name : c.name,
							};
						}),
					};
				}),
			};
		}),
		configId: config.configId,
	};
	lintWizardFormConfig(updatedConfig);
	return updatedConfig;
}
